import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field } from "@/components/UI";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PerformerField } from "@/components/VetTable";
import { today } from "./helpers";
import { InseminationEditModal } from "./InseminationEditModal";
import { InseminationMonthlyCards } from "./InseminationMonthlyCards";


export function Inseminations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [bulls, setBulls] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ animal_type: "inek", animal_count: 1, date: today(), bull_id: "", bull_code: "", method: "suni", semen_type: "konvansiyonel", technician: "", notes: "" });
  const load = () => {
    api.get("/vet/inseminations").then(r => setItems(r.data)).catch(() => {});
    api.get("/semen-stock").then(r => setBulls(r.data)).catch(() => setBulls([]));
  };
  useEffect(() => { load(); }, []);

  // Boğa seçildiğinde semen_type ve bull_code'u otomatik doldur
  const onBullChange = (bid) => {
    if (!bid) {
      setForm(f => ({ ...f, bull_id: "", bull_code: "" }));
      return;
    }
    const b = bulls.find(x => x.id === bid);
    setForm(f => ({
      ...f,
      bull_id: bid,
      bull_code: b?.bull_code || "",
      semen_type: b?.semen_type || f.semen_type,
    }));
  };

  const selectedBull = bulls.find(x => x.id === form.bull_id);
  const willConsume = Number(form.animal_count || 0);
  const notEnough = selectedBull && willConsume > (selectedBull.payet_count || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.animal_count) < 1) return toast.error("En az 1 hayvan girin");
    if (notEnough) return toast.error(`'${selectedBull.bull_name}' boğasında sadece ${selectedBull.payet_count} payet var, ${willConsume} kullanılamaz.`);
    try {
      await api.post("/vet/inseminations", {
        ...form,
        technician: form.technician?.trim() || user?.name || "",
        animal_count: Number(form.animal_count),
        bull_id: form.bull_id || null,
        bull_code: form.bull_code || null,
      });
      toast.success(`${form.animal_count} ${form.animal_type} tohumlaması kaydedildi${form.bull_id ? ` · ${willConsume} payet düşüldü` : ""}`);
      setForm({ ...form, bull_id: "", bull_code: "", notes: "", animal_count: 1 });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Hata"); }
  };

  const del = async (x) => {
    const label = `${x.date} · ${x.animal_count} ${x.animal_type}${x.bull_name ? ` · ${x.bull_name}` : ""}`;
    const warn = x.bull_id && x.payets_used
      ? `\n\nBağlı boğaya ${x.payets_used} payet geri iade edilecek.`
      : "";
    if (!window.confirm(`Bu tohumlama kaydı silinsin mi?\n${label}${warn}`)) return;
    try {
      const r = await api.delete(`/vet/inseminations/${x.id}`);
      if (r.data.payets_restored > 0) {
        toast.success(`Tohumlama silindi · ${r.data.payets_restored} payet stoğa geri iade edildi`);
      } else {
        toast.success("Tohumlama silindi");
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Hata"); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card>
        <h3 className="heading text-lg mb-3">Yeni Tohumlama Kaydı</h3>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hayvan Tipi *">
              <Select data-testid="ins-animal-type" value={form.animal_type} onChange={e => setForm({ ...form, animal_type: e.target.value })}>
                <option value="inek">İnek</option>
                <option value="düve">Düve</option>
              </Select>
            </Field>
            <Field label="Adet *">
              <Input data-testid="ins-count" type="number" min="1" required value={form.animal_count} onChange={e => setForm({ ...form, animal_count: e.target.value })} />
            </Field>
          </div>
          <Field label="Tarih *"><Input type="date" data-testid="ins-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>

          {/* Sperma stoğundan boğa seçimi */}
          <Field label="Kullanılan Boğa (Sperma Stoğundan)">
            <Select data-testid="ins-bull-select" value={form.bull_id} onChange={e => onBullChange(e.target.value)}>
              <option value="">— Boğa seçin (opsiyonel) —</option>
              {bulls.filter(b => b.payet_count > 0).map(b => (
                <option key={b.id} value={b.id}>
                  {b.bull_name} {b.bull_code ? `(${b.bull_code})` : ""} · {b.payet_count} payet · {b.semen_type}
                </option>
              ))}
              {bulls.filter(b => b.payet_count === 0).map(b => (
                <option key={b.id} value={b.id} disabled>
                  {b.bull_name} — STOK BOŞ
                </option>
              ))}
            </Select>
          </Field>
          {selectedBull && (
            <div className={`text-xs px-3 py-2 rounded ${notEnough ? "bg-[#FBECEA] border border-[#E9B5AB] text-[#8B2E1D]" : "bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36]"}`}
                 data-testid="ins-bull-preview">
              {notEnough ? "⚠️ " : "✓ "}
              <b>{selectedBull.bull_name}</b> · Mevcut {selectedBull.payet_count} payet
              {willConsume > 0 && (
                <> · Düşülecek {willConsume} · <b>Kalan: {(selectedBull.payet_count || 0) - willConsume}</b></>
              )}
              {notEnough && <div className="mt-1">Yeterli payet yok. Önce stok girişi yapın.</div>}
            </div>
          )}
          {!form.bull_id && (
            <div className="text-[11px] text-[#8a938c] italic">
              Boğa seçmezseniz sperma stoğundan payet düşülmez (elle girilen "Boğa Kodu" ile devam edebilirsiniz).
            </div>
          )}

          <Field label="Tohum Tipi *">
            <Select data-testid="ins-semen" value={form.semen_type} onChange={e => setForm({ ...form, semen_type: e.target.value })}>
              <option value="konvansiyonel">Konvansiyonel</option>
              <option value="dişi">Dişi (Cinsiyet ayırt.)</option>
              <option value="erkek">Erkek (Cinsiyet ayırt.)</option>
            </Select>
          </Field>
          <Field label="Yöntem">
            <Select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
              <option>suni</option><option>doğal</option>
            </Select>
          </Field>
          <Field label="Boğa Kodu (elle giriş — stok bağlantısı yoksa)"><Input data-testid="ins-bull-code" value={form.bull_code} onChange={e => setForm({ ...form, bull_code: e.target.value })} /></Field>
          <PerformerField label="Tohumlayan (İşlemi Yapan)" user={user} value={form.technician}
            onChange={v => setForm({ ...form, technician: v })} testId="ins-tech" />
          <Field label="Notlar"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button data-testid="ins-submit" type="submit" disabled={notEnough}><Plus size={14} className="inline mr-1" /> Kaydet</Button>
        </form>
      </Card>
      <InseminationMonthlyCards items={items} onEdit={setEditing} onDelete={del} />
      {editing && (
        <InseminationEditModal item={editing} bulls={bulls} user={user}
          onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

